import { buildTitle } from "@/lib/seo-quality";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Phone, MapPin, Clock, CheckCircle2, ArrowRight } from "lucide-react";
import { PageHeader } from "./fordon";
import { getOrt, type OrtData, ORTER } from "@/data/seo-orter";
import { getRapidoModel, type RapidoModel, RAPIDO_MODELS } from "@/data/rapido-models";
import { sizeVerdict, tripMath, winterVerdict, localRoute } from "@/data/ort-modell-content";
import { assessOrtModell } from "@/lib/seo-families/ort-modell";
import { robotsMeta } from "@/lib/seo-quality";

export const Route = createFileRoute("/husbil/$ort/$modell")({
  codeSplitGroupings: [["loader", "component"]],
  loader: ({ params }) => {
    const ort = getOrt(params.ort);
    const modell = getRapidoModel(params.modell);
    if (!ort || !modell) throw notFound();
    return { ort, modell, quality: assessOrtModell(ort, modell) };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const { ort: o, modell: m, quality } = loaderData;
    const url = `https://www.akhusbilfritid.se/husbil/${o.slug}/${m.slug}`;
    const title = buildTitle(`${m.name} ${o.name} — pris, specs och leverans`);
    const desc = `${m.name} till ${o.name}? Officiell Rapido-återförsäljare i Kalix (${o.distanceKm} km från ${o.name}). Nybilsköp, begagnat och transport ordnas.`;
    const faq = [
      {
        q: `Kan jag köpa ${m.name} från ${o.name}?`,
        a: `Ja. Vi ligger ${o.distanceKm} km / ${o.driveMin} min från ${o.name} och tar emot ${o.name}-kunder både för nybilskonfiguration och kontrollerade begagnade ${m.name}. Transport till ${o.name} ordnas mot fast pris.`,
      },
      {
        q: `Vad kostar ${m.name} 2026?`,
        a: `Ny ${m.name}: ${m.priceNew}. Begagnad: ${m.priceUsed}. Vi visar aktuellt lager och konfigurerar nybilspris efter dina val.`,
      },
      {
        q: `Passar ${m.name} för resor från ${o.name}?`,
        a: `${m.perfectFor[0]}. ${o.vinter}`,
      },
      ...m.faq.slice(0, 2),
    ];
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        ...robotsMeta(quality),
        { name: "keywords", content: `${m.keyword} ${o.name.toLowerCase()}, ${m.name} ${o.name.toLowerCase()}, husbil ${o.name.toLowerCase()}, rapido ${o.name.toLowerCase()}` },
        { property: "og:type", content: "website" },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            name: `${m.name} — leverans till ${o.name}`,
            description: m.intro,
            brand: { "@type": "Brand", name: "Rapido" },
            category: m.category,
            offers: {
              "@type": "AggregateOffer",
              priceCurrency: "SEK",
              lowPrice: 550000,
              availability: "https://schema.org/InStock",
              areaServed: o.name,
              seller: { "@type": "AutoDealer", name: "AK Husbil & Fritid", url: "https://www.akhusbilfritid.se" },
            },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Hem", item: "https://www.akhusbilfritid.se" },
              { "@type": "ListItem", position: 2, name: `Husbil ${o.name}`, item: `https://www.akhusbilfritid.se/husbil/${o.slug}` },
              { "@type": "ListItem", position: 3, name: m.name, item: url },
            ],
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="max-w-2xl mx-auto p-8 text-center">
      <h1 className="text-2xl font-bold mb-4">Kombinationen hittades inte</h1>
      <Link to="/rapido" className="text-primary underline">Se alla Rapido-modeller</Link>
    </div>
  ),
  component: OrtModellPage,
});

function OrtModellPage() {
  const { ort: o, modell: m } = Route.useLoaderData() as { ort: OrtData; modell: RapidoModel };
  const size = sizeVerdict(m, o);
  const trip = tripMath(m, o);
  const winter = winterVerdict(m, o);
  const route = localRoute(m, o);
  const pageFaq = [
    {
      q: `Kan jag köpa ${m.name} från ${o.name}?`,
      a: `Ja. Vi ligger ${o.distanceKm} km / ${o.driveMin} min från ${o.name} och tar emot ${o.name}-kunder både för nybilskonfiguration och kontrollerade begagnade ${m.name}. Transport till ${o.name} ordnas från ${trip.transportEstimate}.`,
    },
    {
      q: `Vad kostar ${m.name} 2026?`,
      a: `Ny ${m.name}: ${m.priceNew}. Begagnad: ${m.priceUsed}. Vi visar aktuellt lager och konfigurerar nybilspris efter dina val.`,
    },
    {
      q: `Vad kostar det att köra ${m.name} från ${o.name} till Kalix?`,
      a: `Räknat på ${trip.consumption} går det åt cirka ${trip.liters} liter diesel tur och retur — ungefär ${trip.fuelCost} kr.`,
    },
    ...o.faq.slice(0, 2),
    ...m.faq.slice(0, 2),
  ];
  const otherModels = RAPIDO_MODELS.filter((x) => x.slug !== m.slug).slice(0, 4);
  const otherOrter = ORTER.filter((x) => x.slug !== o.slug).slice(0, 10);

  return (
    <>
      <PageHeader
        label={`${o.region} · ${m.category}`}
        title={`${m.name} i ${o.name}`}
        sub={`${o.distanceKm} km från oss i Kalix — nybilsköp, begagnat och leverans till ${o.name}`}
      />

      <main className="max-w-4xl mx-auto px-4 py-10 space-y-12">
        <nav className="text-sm text-muted-foreground">
          <Link to="/" className="hover:underline">Hem</Link>
          <span className="mx-2">/</span>
          <Link to="/husbil/$ort" params={{ ort: o.slug }} className="hover:underline">Husbil {o.name}</Link>
          <span className="mx-2">/</span>
          <span>{m.name}</span>
        </nav>

        <section>
          <p className="text-lg leading-relaxed">
            {m.intro} För dig som bor i {o.name} innebär det {o.driveMin} minuters bilväg till oss i Kalix — eller så levererar vi {m.name} direkt till {o.name} mot fast pris.
          </p>
        </section>

        <section className="grid sm:grid-cols-4 gap-3">
          <div className="rounded-lg border p-4">
            <MapPin className="w-5 h-5 text-primary mb-2" />
            <div className="text-xs text-muted-foreground">Från {o.name}</div>
            <div className="font-semibold">{o.distanceKm} km</div>
          </div>
          <div className="rounded-lg border p-4">
            <Clock className="w-5 h-5 text-primary mb-2" />
            <div className="text-xs text-muted-foreground">Restid</div>
            <div className="font-semibold">{o.driveMin} min</div>
          </div>
          <div className="rounded-lg border p-4">
            <div className="text-xs text-muted-foreground">Längd</div>
            <div className="font-semibold">{m.lengthCm} cm</div>
          </div>
          <div className="rounded-lg border p-4">
            <div className="text-xs text-muted-foreground">Totalvikt</div>
            <div className="font-semibold">{m.totalWeightKg}</div>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4">{m.name} — snabbfakta</h2>
          <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
            <div className="flex justify-between border-b pb-2"><dt className="text-muted-foreground">Chassi</dt><dd className="font-medium">{m.chassis}</dd></div>
            <div className="flex justify-between border-b pb-2"><dt className="text-muted-foreground">Sängar</dt><dd className="font-medium">{m.beds}</dd></div>
            <div className="flex justify-between border-b pb-2"><dt className="text-muted-foreground">Sover</dt><dd className="font-medium">{m.sleeps}</dd></div>
            <div className="flex justify-between border-b pb-2"><dt className="text-muted-foreground">Värme</dt><dd className="font-medium">{m.heating}</dd></div>
            <div className="flex justify-between border-b pb-2"><dt className="text-muted-foreground">Färskvatten</dt><dd className="font-medium">{m.fresh}</dd></div>
            <div className="flex justify-between border-b pb-2"><dt className="text-muted-foreground">Batteri</dt><dd className="font-medium">{m.battery}</dd></div>
            <div className="flex justify-between border-b pb-2"><dt className="text-muted-foreground">Pris ny</dt><dd className="font-medium">{m.priceNew}</dd></div>
            <div className="flex justify-between border-b pb-2"><dt className="text-muted-foreground">Pris begagnad</dt><dd className="font-medium">{m.priceUsed}</dd></div>
          </dl>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4">Varför {o.name}-kunder väljer {m.name}</h2>
          <ul className="space-y-2">
            {m.strengths.map((s, i) => (
              <li key={i} className="flex gap-3">
                <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <span>{s}</span>
              </li>
            ))}
          </ul>
          <p className="mt-4 text-muted-foreground">{o.vinter}</p>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4">Passar storleken i {o.name}? ({size.klass}, {size.lengthM} m)</h2>
          <ul className="space-y-2 text-muted-foreground">
            <li>{size.parkering}</li>
            <li>{size.stallplats}</li>
            <li>{size.fargja}</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4">Vad kostar resan {o.name}–Kalix?</h2>
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="rounded-lg border p-4">
              <div className="text-xs text-muted-foreground">Förbrukning {m.name}</div>
              <div className="font-semibold">{trip.consumption}</div>
            </div>
            <div className="rounded-lg border p-4">
              <div className="text-xs text-muted-foreground">Tur & retur {o.name}</div>
              <div className="font-semibold">{trip.liters} l ≈ {trip.fuelCost} kr</div>
            </div>
            <div className="rounded-lg border p-4">
              <div className="text-xs text-muted-foreground">Transport till dörren</div>
              <div className="font-semibold">från {trip.transportEstimate}</div>
            </div>
          </div>
          <p className="mt-3 text-sm text-muted-foreground">
            Räknat på {o.distanceKm} km enkel väg, dieselpris 19,50 kr/l och normal landsvägskörning. Transportpriset är
            en riktlinje — vi lämnar alltid fast pris innan köp. Vill du räkna på hela ägandet finns vår{" "}
            <Link to="/verktyg/manadskostnad-husbil" className="underline hover:text-primary">månadskostnadskalkyl</Link>.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4">{m.name} i {o.region}-vintern</h2>
          <div className="space-y-3 text-muted-foreground">
            <p>{winter.heating}</p>
            <p>{winter.batteryNote}</p>
            <p>{winter.waterNote}</p>
            <p>{winter.verdict}</p>
          </div>
        </section>

        <section className="rounded-xl border bg-muted/30 p-6">
          <h2 className="text-2xl font-bold mb-3">{route.title}</h2>
          <p className="text-muted-foreground">{route.text}</p>
          <div className="mt-4 flex flex-wrap gap-2 text-sm">
            {o.campingPlaces.map((c) => (
              <span key={c} className="rounded-full border bg-background px-3 py-1">{c}</span>
            ))}
          </div>
          <Link to="/campingtips" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline">
            Fler campingtips i Norrbotten <ArrowRight className="w-4 h-4" />
          </Link>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4">Vanliga frågor från {o.name}</h2>
          <div className="space-y-4">
            {pageFaq.map((f) => (
              <details key={f.q} className="rounded-lg border p-4">
                <summary className="cursor-pointer font-semibold">{f.q}</summary>
                <p className="mt-2 text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4">Leverans till {o.name}</h2>
          <p>
            Vi levererar {m.name} till {o.name} mot fast pris — hela processen kan skötas digitalt om du vill: videosamtal, digital signering, transport till din uppfart. Många {o.name}-kunder ser fordonet först vid leverans. {o.varforOss[0]}.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4">Andra Rapido-modeller för {o.name}</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {otherModels.map((om) => (
              <Link
                key={om.slug}
                to="/husbil/$ort/$modell"
                params={{ ort: o.slug, modell: om.slug }}
                className="rounded-lg border p-4 hover:border-primary hover:bg-muted/40"
              >
                <div className="font-semibold">{om.name} i {o.name}</div>
                <div className="text-sm text-muted-foreground">{om.category} · {om.priceUsed}</div>
              </Link>
            ))}
          </div>
          <h3 className="text-lg font-bold mt-8 mb-3">{m.name} på andra orter</h3>
          <div className="flex flex-wrap gap-2 text-sm">
            {otherOrter.map((oo) => (
              <Link
                key={oo.slug}
                to="/husbil/$ort/$modell"
                params={{ ort: oo.slug, modell: m.slug }}
                className="rounded-full border px-3 py-1.5 hover:border-primary hover:bg-muted/40"
              >
                {m.name} {oo.name}
              </Link>
            ))}
          </div>
        </section>


        <section className="rounded-xl border-2 border-primary/20 bg-muted/40 p-6">
          <h2 className="text-xl font-bold mb-2">Se aktuellt lager — {m.name}</h2>
          <p className="text-muted-foreground mb-4">
            Vi uppdaterar lagret dagligen på Fyndanu. Sök på "{m.name}" för att se just den modellen.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/fordon" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary text-primary-foreground font-semibold hover:opacity-90">
              Se alla husbilar <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/rapido/modeller/$model" params={{ model: m.slug }} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border font-semibold hover:bg-muted">
              Full {m.name}-info
            </Link>
          </div>
        </section>

        <section className="rounded-xl bg-primary text-primary-foreground p-8 text-center">
          <h2 className="text-2xl font-bold mb-3">{m.name} till {o.name} — ring oss</h2>
          <p className="mb-6 opacity-90">Vi svarar samma dag. Fråga om konfiguration, transport eller inbyte.</p>
          <a href="tel:0708629743" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-background text-foreground font-semibold hover:opacity-90">
            <Phone className="w-4 h-4" /> 070-862 97 43
          </a>
        </section>
      </main>
    </>
  );
}
